// we want the yaml generated to be in the order we describe
/* eslint-disable sort-keys-fix/sort-keys-fix */

import { platformListTablesTeam } from '@cloudquery/api-platform';
import { fetchAllPages } from 'shared/base';

import { ReportFilter } from '../../types/filters';
import { ReportYamlItem } from '../../types/report';

const INTEGRATIONS_TABLES = {
  anchore_accounts: ['anchore_accounts'],
  apigee_developers: ['apigee_developers'],
  aws_iam_users: ['aws_iam_users'],
  buildkite_team_members: ['buildkite_team_members'],
  cloudflare_account_members: ['cloudflare_account_members'],
  datadog_users: ['datadog_users', 'datadog_roles'],
  digitalocean_accounts: ['digitalocean_accounts'],
  entraid_users: ['entraid_users', 'entraid_directoryroles', 'entraid_directoryrole_members'],
  fastly_account_users: ['fastly_account_users'],
  github_organization_members: ['github_organization_members', 'github_organization_memberships'],
  hubspot_crm_owners: ['hubspot_crm_owners'],
  okta_users: ['okta_users'],
  opsgenie_users: ['opsgenie_users'],
  pagerduty_users: ['pagerduty_users'],
  snowflake_users: ['snowflake_users', 'snowflake_user_grants'],
  vercel_teams: ['vercel_teams', 'vercel_team_members'],
  wiz_users: ['wiz_users'],
} as const;

type IntegrationTableName = keyof typeof INTEGRATIONS_TABLES;

type QueryTemplates = {
  all_user_accounts: {
    fromBlock: string;
    integrationName: string;
    roleColumn: string;
    userIdColumn: string;
  };
  user_accounts_count: {
    integrationName: string;
    userIdColumn: string;
  };
};

const INTEGRATIONS_TABLES_QUERY_TEMPLATES: Record<IntegrationTableName, QueryTemplates> = {
  anchore_accounts: {
    all_user_accounts: {
      fromBlock: 'anchore_accounts',
      integrationName: 'anchore',
      roleColumn: 'type',
      userIdColumn: 'email',
    },
    user_accounts_count: {
      integrationName: 'anchore',
      userIdColumn: 'email',
    },
  },
  apigee_developers: {
    all_user_accounts: {
      fromBlock: 'apigee_developers',
      integrationName: 'apigee',
      roleColumn: "'Various Roles'",
      userIdColumn: 'email',
    },
    user_accounts_count: {
      integrationName: 'apigee',
      userIdColumn: 'email',
    },
  },
  aws_iam_users: {
    all_user_accounts: {
      fromBlock: `
        (
          with roles as (
              select arn, role_name, JSONExtractString(JSONExtractString(statement, 'Principal'), 'AWS') as aws_principal from aws_iam_roles
              ARRAY JOIN JSONExtractArrayRaw(COALESCE(assume_role_policy_document, '{}'), 'Statement') as statement
              where aws_principal != ''
          )
          select users.arn as user_name, groupConcat(', ')(r.role_name) as role_name from aws_iam_users users
          left join roles r on users.arn = r.aws_principal
          group by users.arn
        )
      `,
      integrationName: 'aws',
      roleColumn: 'role_name',
      userIdColumn: 'user_name',
    },
    user_accounts_count: {
      integrationName: 'aws',
      userIdColumn: 'user_name',
    },
  },
  buildkite_team_members: {
    all_user_accounts: {
      fromBlock: 'buildkite_team_members',
      integrationName: 'buildkite',
      roleColumn: 'role',
      userIdColumn: 'user_name',
    },
    user_accounts_count: {
      integrationName: 'buildkite',
      userIdColumn: 'user_name',
    },
  },
  cloudflare_account_members: {
    all_user_accounts: {
      fromBlock: `(
        SELECT
          *,
          JSONExtractString(role, 'name') as role_name
        FROM
          cloudflare_account_members
        ARRAY JOIN (JSONExtractArrayRaw(coalesce(roles, '{}'))) AS role
      )`,
      integrationName: 'cloudflare',
      roleColumn: 'role_name',
      userIdColumn: "JSONExtractString(user, 'email')",
    },
    user_accounts_count: {
      integrationName: 'cloudflare',
      userIdColumn: "JSONExtractString(user, 'email')",
    },
  },
  datadog_users: {
    all_user_accounts: {
      fromBlock: `
        (
          SELECT
            user_roles.email as "User ID",
            JSONExtractString(r.attributes, 'name') as "Role"
          FROM
            (
              SELECT
                JSONExtractString(role, 'id') as role_id,
                JSONExtractString(u.attributes, 'email') as email
              FROM
                datadog_users u
              ARRAY JOIN
                JSONExtractArrayRaw(JSONExtractRaw(coalesce(u.relationships, '{}'), 'roles'), 'data') as role
            ) AS user_roles
            INNER JOIN
              datadog_roles r
            ON
              r.id = user_roles.role_id
        )
      `,
      integrationName: 'datadog',
      roleColumn: 'Role',
      userIdColumn: '"User ID"',
    },
    user_accounts_count: {
      integrationName: 'datadog',
      userIdColumn: "JSONExtractString(attributes, 'email')",
    },
  },
  digitalocean_accounts: {
    all_user_accounts: {
      fromBlock: 'digitalocean_accounts',
      integrationName: 'digital ocean',
      roleColumn: "'Various Roles'",
      userIdColumn: 'email',
    },
    user_accounts_count: {
      integrationName: 'digital ocean',
      userIdColumn: 'email',
    },
  },
  entraid_users: {
    all_user_accounts: {
      fromBlock: `
       (
        SELECT u.user_principal_name as user_principal_name, groupConcat(', ')(r.display_name) as role_name FROM entraid_users u
        LEFT JOIN entraid_directoryrole_members m ON u.id = m.id
        LEFT JOIN entraid_directoryroles r ON m.directoryrole_id = r.id
        GROUP BY u.user_principal_name
       )
      `,
      integrationName: 'entraid',
      roleColumn: 'role_name',
      userIdColumn: 'user_principal_name',
    },
    user_accounts_count: {
      integrationName: 'entraid',
      userIdColumn: 'user_principal_name',
    },
  },
  fastly_account_users: {
    all_user_accounts: {
      fromBlock: 'fastly_account_users',
      integrationName: 'fastly',
      roleColumn: "'Various Roles'",
      userIdColumn: 'user_id',
    },
    user_accounts_count: {
      integrationName: 'fastly',
      userIdColumn: 'user_id',
    },
  },
  github_organization_members: {
    all_user_accounts: {
      fromBlock:
        'github_organization_members JOIN github_organization_memberships ON github_organization_members.org = github_organization_memberships.org AND github_organization_members.id = github_organization_memberships.user_id AND github_organization_memberships.role IS NOT NULL',
      integrationName: 'github',
      roleColumn: `role`,
      userIdColumn: 'COALESCE(email, login)',
    },
    user_accounts_count: {
      integrationName: 'github',
      userIdColumn: 'login',
    },
  },
  hubspot_crm_owners: {
    all_user_accounts: {
      fromBlock: 'hubspot_crm_owners',
      integrationName: 'hubspot',
      roleColumn: "'Various Roles'",
      userIdColumn: 'email',
    },
    user_accounts_count: {
      integrationName: 'hubspot',
      userIdColumn: 'email',
    },
  },
  okta_users: {
    all_user_accounts: {
      fromBlock: 'okta_users',
      integrationName: 'okta',
      roleColumn: "'Various Roles'",
      userIdColumn: "JSONExtractString(coalesce(profile, '{}'), 'email')",
    },
    user_accounts_count: {
      integrationName: 'okta',
      userIdColumn: "JSONExtractString(coalesce(profile, '{}'), 'email')",
    },
  },
  opsgenie_users: {
    all_user_accounts: {
      fromBlock: 'opsgenie_users',
      integrationName: 'ops genie',
      roleColumn: 'role',
      userIdColumn: 'username',
    },
    user_accounts_count: {
      integrationName: 'ops genie',
      userIdColumn: 'username',
    },
  },
  pagerduty_users: {
    all_user_accounts: {
      fromBlock: 'pagerduty_users',
      integrationName: 'pagerduty',
      roleColumn: 'role',
      userIdColumn: 'email',
    },
    user_accounts_count: {
      integrationName: 'pagerduty',
      userIdColumn: 'email',
    },
  },
  snowflake_users: {
    all_user_accounts: {
      fromBlock: `(
        SELECT
          u._cq_id,
          u.email as "Email",
          ug.role as "Role"
        FROM
          snowflake_users AS u
        INNER JOIN
          snowflake_user_grants AS ug
        ON
          ug._cq_parent_id = u._cq_id
      )`,
      integrationName: 'snowflake',
      roleColumn: '"Role"',
      userIdColumn: '"Email"',
    },
    user_accounts_count: {
      integrationName: 'snowflake',
      userIdColumn: 'email',
    },
  },
  vercel_teams: {
    all_user_accounts: {
      fromBlock: `(
        SELECT
          t._cq_id,
          u.email as "Email",
          t.name as "Role"
        FROM
          vercel_teams AS t
        INNER JOIN
          vercel_team_members AS u
        ON
          u._cq_parent_id = t._cq_id
      )`,
      integrationName: 'vercel',
      roleColumn: '"Role"',
      userIdColumn: '"Email"',
    },
    user_accounts_count: {
      integrationName: 'vercel',
      userIdColumn: 'email',
    },
  },
  wiz_users: {
    all_user_accounts: {
      fromBlock: 'wiz_users',
      integrationName: 'wiz',
      roleColumn: 'role',
      userIdColumn: 'email',
    },
    user_accounts_count: {
      integrationName: 'wiz',
      userIdColumn: 'email',
    },
  },
};

const USER_ACCOUNTS_BY_INTEGRATION_QUERY = (availableTables: IntegrationTableName[]) => {
  return availableTables
    .map((availableTable) => {
      const queryTemplate = INTEGRATIONS_TABLES_QUERY_TEMPLATES[availableTable].user_accounts_count;

      return `
        SELECT
          '${availableTable}' as _table_name,
          '${queryTemplate.integrationName}' as Integration_Name,
          count(DISTINCT ${queryTemplate.userIdColumn}) as "Count"
        FROM ${availableTable}
        WHERE 1=1 and _table_name = '?'
        `;
    })
    .join(' UNION ALL ');
};

const ALL_USER_ACCOUNTS_QUERY = (availableTables: IntegrationTableName[]) =>
  availableTables
    .map((availableTable) => {
      const queryTemplate = INTEGRATIONS_TABLES_QUERY_TEMPLATES[availableTable].all_user_accounts;

      return `SELECT
          '${availableTable}' as _table_name,
          '${queryTemplate.integrationName}' as Integration_Name,
          ${queryTemplate.userIdColumn} as "User ID",
          ${queryTemplate.roleColumn} as "Role",
          lower(concat(
            coalesce(Integration_Name, ''),
            coalesce("User ID", ''),
            coalesce("Role", '')
          )) as search_string
        FROM ${queryTemplate.fromBlock}
        WHERE "User ID" IS NOT NULL and _table_name = '?'
      `;
    })
    .filter(Boolean)
    .join(' UNION ALL ');

const ensureDependantTables = (
  table: unknown,
  allTables: string[],
): table is IntegrationTableName =>
  typeof table === 'string' &&
  INTEGRATIONS_TABLES[table as IntegrationTableName]?.every((dependantTable) =>
    allTables.includes(dependantTable),
  );

const getReportWidgets = (availableTables: IntegrationTableName[]): ReportYamlItem[] => [
  {
    title: 'User accounts by integration',
    display: {
      type: 'pie_chart',
    },
    query: `
    select Integration_Name, Count
    from (
      ${USER_ACCOUNTS_BY_INTEGRATION_QUERY(availableTables)}
    )
    order by Count desc
    `,
  },
  {
    title: 'All users accounts',
    display: {
      type: 'table',
      width: '100%',
    },
    query: `
    select Integration_Name, "User ID", Role
    from (
      ${ALL_USER_ACCOUNTS_QUERY(availableTables)}
    )
    order by Role
    `,
  },
];

const getReportFilters = (availableTables: IntegrationTableName[]): ReportFilter[] => [
  {
    type: 'generic',
    filterExpression: "_table_name = '?'",
    filterLabel: 'Integration',
    options: availableTables.map((table) => ({
      label: table,
      value: table,
    })),
  },
];

export async function getReportItems(team: string) {
  const data = await fetchAllPages((page) =>
    platformListTablesTeam(team, { page, per_page: 1000 }),
  );
  const allTables = data.data.items.map((item: { name: string }) => item.name);
  const availableTables = allTables.filter((tableName: string) =>
    ensureDependantTables(tableName, allTables),
  );

  const filters = availableTables.length > 0 ? getReportFilters(availableTables) : {};
  const widgets = availableTables.length > 0 ? getReportWidgets(availableTables) : [];

  return {
    filters,
    widgets,
  };
}
